import { useEffect, useRef, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import BloodHelixHero, { usingStaticFallback } from "./BloodHelixHero";

export default function HeroLayout() {
    const [paused, setPaused] = useState(false);
    const [pastHero, setPastHero] = useState(false);
    const [helixVisible, setHelixVisible] = useState(true);
    const scrollProgressRef = useRef(0);
    const helixSentinelRef = useRef(null);
    const location = useLocation();
    // No live scene to freeze when the static fallback is showing.
    const showFreezeToggle = !usingStaticFallback();

    useEffect(() => {
        if (location.pathname !== "/") {
            scrollProgressRef.current = 0;
        }
    }, [location.pathname]);

    useEffect(() => {
        const onScroll = () => {
            setPastHero(window.scrollY > 0);
        };
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, [location.pathname]);

    // Auto-pause helix when scrolled fully out of view for CPU savings.
    // Sentinel spans the hero viewport; rootMargin resumes 200px early.
    useEffect(() => {
        const el = helixSentinelRef.current;
        if (!el) return;
        const observer = new IntersectionObserver(
            ([entry]) => setHelixVisible(entry.isIntersecting),
            { rootMargin: "200px 0px 0px 0px" }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    return (
        <>
            <div style={{ position: "fixed", inset: 0, zIndex: 0, overflow: "hidden" }}>
                <BloodHelixHero paused={paused || !helixVisible} scrollProgressRef={scrollProgressRef} />
            </div>
            <div style={{ touchAction: "pan-y", position: "relative" }}>
                <div
                    ref={helixSentinelRef}
                    style={{ position: "absolute", top: 0, height: "100vh", width: 1, pointerEvents: "none" }}
                    aria-hidden="true"
                />
                <Outlet context={{ scrollProgressRef, paused, setPaused }} />
            </div>
            {showFreezeToggle && (
            <button
                type="button"
                onClick={() => setPaused((p) => !p)}
                aria-label={paused ? "Resume background animation" : "Freeze background animation"}
                title={paused ? "Resume animation" : "Freeze animation"}
                className={`hero-freeze-toggle hero-freeze-toggle--floating ${paused ? 'hero-freeze-toggle--paused' : ''} ${pastHero ? 'hero-freeze-toggle--hidden' : ''}`}
            >
                {paused ? (
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor" aria-hidden="true">
                        <path d="M3 2 L11 7 L3 12 Z" />
                    </svg>
                ) : (
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor" aria-hidden="true">
                        <rect x="3" y="2.25" width="2.75" height="9.5" rx="0.5" />
                        <rect x="8.25" y="2.25" width="2.75" height="9.5" rx="0.5" />
                    </svg>
                )}
            </button>
            )}
        </>
    );
}
